import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { apiClient } from '../../services/apiClient';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Modal for selecting company documents (Past Performance or Resumes)
 * @param {string} documentType - 'past_performance' or 'resume'
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Close modal callback
 * @param {function} onSelect - Callback when documents are selected (receives array of document IDs)
 * @param {Array} selectedIds - Currently selected document IDs
 */
const CompanyDocumentSelectionModal = ({
  documentType,
  isOpen,
  onClose,
  onSelect,
  selectedIds = []
}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [tempSelected, setTempSelected] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadDocuments();
      setTempSelected([...selectedIds]);
    }
  }, [isOpen, documentType]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getCompanyDocuments(documentType);
      if (response.success) {
        // Only show completed documents
        const completedDocs = (response.data.documents || []).filter(
          doc => doc.processing_status === 'completed'
        );
        setDocuments(completedDocs);
      }
    } catch (err) {
      console.error('Failed to load company documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDocument = (documentId) => {
    setTempSelected(prev => {
      if (prev.includes(documentId)) {
        return prev.filter(id => id !== documentId);
      } else {
        return [...prev, documentId];
      }
    });
  };

  const handleConfirm = () => {
    onSelect(tempSelected);
    onClose();
  };

  const filteredDocuments = documents
    .filter(doc => {
      const searchLower = search.toLowerCase();
      return (
        doc.document_name.toLowerCase().includes(searchLower) ||
        doc.pp_project_name?.toLowerCase().includes(searchLower) ||
        doc.pp_customer?.toLowerCase().includes(searchLower) ||
        doc.resume_person_name?.toLowerCase().includes(searchLower) ||
        doc.resume_role_title?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      // Sort by document name alphabetically
      return (a.document_name || '').localeCompare(b.document_name || '');
    });

  if (!isOpen) return null;

  const title = documentType === 'past_performance'
    ? 'Select Past Performance Documents'
    : 'Select Resumes';

  const icon = documentType === 'past_performance' ? 'trophy' : 'user-tie';
  const color = documentType === 'past_performance' ? 'yellow' : 'blue';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900 flex items-center">
            <FontAwesomeIcon icon={icon} className={`mr-2 text-${color}-500`} />
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700"
          >
            <FontAwesomeIcon icon="times" className="text-xl" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-slate-200">
          <input
            type="text"
            placeholder="Search documents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        {/* Document List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8">
              <LoadingSpinner />
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FontAwesomeIcon icon="folder-open" className="text-4xl mb-2" />
              <p>No {documentType === 'past_performance' ? 'past performance documents' : 'resumes'} available</p>
              <p className="text-sm mt-2">Upload documents in the Admin Panel &gt; Company Data tab</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredDocuments.map(doc => (
                <div
                  key={doc.document_id}
                  onClick={() => handleToggleDocument(doc.document_id)}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    tempSelected.includes(doc.document_id)
                      ? `border-${color}-500 bg-${color}-50`
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <input
                        type="checkbox"
                        checked={tempSelected.includes(doc.document_id)}
                        onChange={() => {}}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{doc.document_name}</h3>

                        {documentType === 'past_performance' && (
                          <>
                            {doc.pp_project_name && (
                              <p className="text-sm text-slate-600 mt-1">
                                <strong>Project:</strong> {doc.pp_project_name}
                              </p>
                            )}
                            {doc.pp_customer && (
                              <p className="text-sm text-slate-600">
                                <strong>Customer:</strong> {doc.pp_customer}
                              </p>
                            )}
                            {doc.pp_year && (
                              <p className="text-sm text-slate-600">
                                <strong>Year:</strong> {doc.pp_year}
                              </p>
                            )}
                            {doc.pp_description && (
                              <p className="text-sm text-slate-500 mt-2 line-clamp-2">
                                {doc.pp_description}
                              </p>
                            )}
                          </>
                        )}

                        {documentType === 'resume' && (
                          <>
                            {doc.resume_person_name && (
                              <p className="text-sm text-slate-600 mt-1">
                                <strong>Name:</strong> {doc.resume_person_name}
                              </p>
                            )}
                            {doc.resume_role_title && (
                              <p className="text-sm text-slate-600">
                                <strong>Role:</strong> {doc.resume_role_title}
                              </p>
                            )}
                            {doc.resume_years_experience && (
                              <p className="text-sm text-slate-600">
                                <strong>Experience:</strong> {doc.resume_years_experience} years
                              </p>
                            )}
                            {doc.resume_key_skills && (
                              <p className="text-sm text-slate-500 mt-2 line-clamp-2">
                                <strong>Skills:</strong> {doc.resume_key_skills}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            {tempSelected.length} selected
          </div>
          <div className="flex space-x-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded hover:bg-slate-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className={`px-4 py-2 bg-${color}-500 text-white rounded hover:bg-${color}-600 transition-colors`}
            >
              Add Selected ({tempSelected.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDocumentSelectionModal;
